/* eslint-disable no-console */
import { useCallback } from 'react';
import { useCanSubscribeProjectRealtime } from 'abilities/projects';
import {
  EntityChangeType,
  ProjectTypeEnum,
} from '../../../../../server/graphql/__generated.types';
import { useProjectAccessEntityChangedSubscription } from '../../ProjectAccess/operation.generated';
import useIsOutOfOrderEvent from '../../../../util/subscriptionUtils';
import { composeProjectAccessId } from '../../../../../server/graphql/project/utils/projectAccessIdUtilities';
import useProjectAccessCacheUtilities from '../../useProjectAccessCacheUtilities';
import { useUserContext } from '../../../../context/user/UserContext';
import { usePromptsContext } from '../../../../context/prompts/PromptsContext';
import { useAppSelector } from '../../../../store/hooks';

const useProjectAccessEntityChanged = (
  projectId: string | null,
  type: ProjectTypeEnum | null
) => {
  const { data: userQuery } = useUserContext();
  const viewerId = userQuery?.user.id;
  const canSubscribeProjectRealtime = useCanSubscribeProjectRealtime();

  const drivesState = useAppSelector(appState => appState.drives);
  const [, dispatchPrompt] = usePromptsContext();

  const handleProjectAccessModified = useCallback(() => {
    dispatchPrompt({
      type: 'OpenEntityChangedNotificationPrompt',
      payload: {
        header: 'Permissions Error',
        body: 'Your access has been modified by the project owner.',
        linkUrl: `/dashboard/studio/${projectId}`,
      },
    });
  }, [dispatchPrompt, projectId]);

  const handleProjectAccessDeleted = useCallback(() => {
    dispatchPrompt({
      type: 'OpenEntityChangedNotificationPrompt',
      payload: {
        header: 'Permissions Error',
        body: 'Your access has been modified by the project owner.',
        linkUrl: '/dashboard/projects/personal',
      },
    });
  }, [dispatchPrompt]);

  const {
    readProjectAccessFragment,
    removeProjectAccessFromCache,
    updateProjectAccessInCache,
    createProjectAccessInCache,
  } = useProjectAccessCacheUtilities();
  const isOutOfOrderEvent = useIsOutOfOrderEvent();

  return useProjectAccessEntityChangedSubscription({
    variables: { inputs: { projectId, type } },
    skip: !canSubscribeProjectRealtime,
    onData: data => {
      const projectAccessEvent = data.data?.data?.projectAccessEntityChanged;
      if (!projectAccessEvent) {
        console.error('projectAccessEvent not found');
        return;
      }
      const { projectAccessEntity, ...event } = projectAccessEvent;
      const changeType = event?.changeType;

      if (!projectAccessEntity) {
        console.error('projectAccessEntity not found');
        return;
      }
      // check the timing of events
      const outOfOrderEvent = isOutOfOrderEvent(
        projectAccessEntity.id,
        projectAccessEvent.eventTime
      );
      if (outOfOrderEvent) {
        console.info(
          'out of order event occurred within useProjectAccessEntityChanged'
        );
        return;
      }

      if (!viewerId) {
        return;
      }
      const projectAccessId = composeProjectAccessId({
        projectId: projectAccessEntity.projectId,
        userId: viewerId,
        teamId: projectAccessEntity.teamId ?? null,
        driveId: drivesState.currentDrive?.id || null,
        folderId: drivesState.currentFolder?.id || null,
      });
      const existingProjectAccess = readProjectAccessFragment(projectAccessId);

      if (!existingProjectAccess) {
        console.error('existingProjectAccess not found in cache');
        return;
      }

      switch (changeType) {
        case EntityChangeType.Created: {
          createProjectAccessInCache(
            projectAccessEntity,
            viewerId,
            projectAccessId
          );
          break;
        }
        case EntityChangeType.Updated: {
          updateProjectAccessInCache(
            projectAccessEntity,
            viewerId,
            projectAccessId
          );
          if (
            projectId &&
            (viewerId === projectAccessEntity.userId ||
              (projectAccessEntity.userId === null &&
                projectAccessEntity.teamId))
          ) {
            // only open prompt if within [projectId]
            handleProjectAccessModified();
          }

          break;
        }
        case EntityChangeType.Deleted: {
          removeProjectAccessFromCache(
            projectAccessEntity,
            viewerId,
            projectAccessId
          );
          if (
            projectId &&
            (viewerId === projectAccessEntity.userId ||
              (projectAccessEntity.userId === null &&
                projectAccessEntity.teamId))
          ) {
            // only open prompt if within [projectId]
            const teamWideAccess = existingProjectAccess.teamWideAccessRole;
            if (viewerId === projectAccessEntity.userId && teamWideAccess) {
              // A: if there is still a team-wide access, we only want to refresh page
              handleProjectAccessModified();
            } else {
              // B: if no team-wide access we want to redirect to projects dashboard
              handleProjectAccessDeleted();
            }
          }
          break;
        }
        default:
          // this default should not be hit
          console.error(
            `error: something went wrong in useProjectAccessEntityChangedSubscription`
          );
      }
    },
  });
};

export default useProjectAccessEntityChanged;
